import { graphqlQuery } from '@/lib/api/graphql';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import type { AllocationHistoryEntry, TransactionType } from '@/types/transaction';
import { useModeAwareQuery } from './useModeAwareQuery';

// --- GraphQL types ---

interface AllocationHistoryGraphQLNode {
  allocationHistoryId: number;
  customerId: number;
  customerName: string | null;
  customerAccountNumber: string | null;
  transactionId: number;
  transactionReference: string | null;
  allocationDate: string | null;
  allocationValue: number | null;
  currencyAllocationValue: number | null;
  allocationRef: string | null;
  userName: string | null;
  transactionTypeCode: string | null;
  transactionTypeName: string | null;
  currencyCode: string | null;
  homeCurrencyCode: string | null;
}

interface AllocationHistoryListGraphQLResponse {
  allocationHistory: {
    allocationHistoryList: {
      edges: Array<{ node: AllocationHistoryGraphQLNode }>;
      pageInfo: {
        hasNextPage: boolean;
        hasPreviousPage: boolean;
      };
    };
  };
}

// --- GraphQL query ---

const ALLOCATION_HISTORY_LIST_QUERY = `
  query AllocationHistoryList($first: Int, $filter: AllocationHistoryListFilterInput!) {
    allocationHistory {
      allocationHistoryList(first: $first, filter: $filter) {
        edges {
          node {
            allocationHistoryId
            customerId
            customerName
            customerAccountNumber
            transactionId
            transactionReference
            allocationDate
            allocationValue
            currencyAllocationValue
            allocationRef
            userName
            transactionTypeCode
            transactionTypeName
            currencyCode
            homeCurrencyCode
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
`;

// --- Mapping ---

function mapGraphQLNodeToAllocationHistoryEntry(node: AllocationHistoryGraphQLNode): AllocationHistoryEntry {
  const allocationValue = node.allocationValue ?? 0;
  const transactionType: TransactionType = (node.transactionTypeCode as TransactionType) ?? 'invoice';
  const homeCurrency = node.homeCurrencyCode ?? 'GBP';
  const transactionCurrency = node.currencyCode ?? homeCurrency;
  const hasForeignCurrency = node.currencyAllocationValue !== null && node.currencyAllocationValue !== 0;
  const currencyCurrency = hasForeignCurrency ? transactionCurrency : homeCurrency;

  return {
    id: node.allocationHistoryId,
    accountNumber: node.customerAccountNumber ?? '',
    customerName: node.customerName ?? '',
    transactionDate: node.allocationDate ?? '',
    type: transactionType,
    referenceNumber: node.transactionReference ?? '',
    gross: allocationValue,
    allocatedValue: allocationValue,
    allocatedDate: node.allocationDate ?? '',
    currency: homeCurrency,
    currencyGross: node.currencyAllocationValue ?? allocationValue,
    currencyAllocValue: node.currencyAllocationValue ?? allocationValue,
    currencyCurrency,
    allocationRef: node.allocationRef ?? '',
    userName: node.userName ?? '',
  };
}

// --- Mock data ---

const MOCK_ALLOCATION_HISTORY: AllocationHistoryEntry[] = [];

// --- Hook ---

/**
 * Fetches allocation history for a given transaction ID.
 * Uses the MainTransactionId filter which finds all allocation history entries
 * sharing the same AllocationRef as the given transaction.
 */
export function useAllocationHistoryGraphQL(transactionId: number | null) {
  const apiConfig = useApiConfig();

  return useModeAwareQuery<AllocationHistoryEntry[], Error>(
    ['allocation-history-graphql', transactionId],
    async ({ signal }) => {
      if (!transactionId) return [];

      const variables: Record<string, unknown> = {
        first: 100,
        filter: {
          mainTransactionId: transactionId,
        },
      };

      const response = await graphqlQuery<AllocationHistoryListGraphQLResponse>(
        apiConfig,
        ALLOCATION_HISTORY_LIST_QUERY,
        variables,
        signal,
      );

      const edges = response.allocationHistory.allocationHistoryList.edges;
      return edges.map((x) => mapGraphQLNodeToAllocationHistoryEntry(x.node));
    },
    MOCK_ALLOCATION_HISTORY,
    {
      enabled: transactionId !== null,
      staleTime: 30_000,
    },
  );
}
